// src/hooks/useExploreData.js
// Custom hook to manage Explore mode data for the landing page

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  getProgramsForClubs, 
  getCapacityForPrograms,
  filterPrograms 
} from '../api/programs';
import { DEFAULT_EXPLORE_FILTERS } from '../constants/exploreFilters';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

/**
 * Custom hook for managing explore mode data
 * Fetches clubs with their programs and handles filtering
 */
export const useExploreData = (userLocation, options = {}) => {
  const {
    initialFilters = DEFAULT_EXPLORE_FILTERS,
    rangeInMiles = 50,
    enabled = true
  } = options;

  // State
  const [clubs, setClubs] = useState([]);
  const [clubPrograms, setClubPrograms] = useState({}); // Map of clubId -> programs[]
  const [capacityMap, setCapacityMap] = useState({}); // Map of programId -> capacity status
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Refs for debouncing
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = useCallback((lat1, lng1, lat2, lng2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);

  // Fetch clubs from the API
  const fetchClubs = useCallback(async () => {
    if (!userLocation || !enabled) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('lat', userLocation.lat);
      params.append('lng', userLocation.lng);
      params.append('maxDistance', rangeInMiles * 1609.34); // Convert miles to meters

      // Apply sport filter at club level if set
      if (filters.sport && filters.sport !== 'All') {
        params.append('sport', filters.sport);
      }

      // Apply search query for club name
      if (filters.searchQuery && filters.searchQuery.trim()) {
        params.append('name', filters.searchQuery.trim());
      }

      const response = await fetch(
        `${backendUrl}/api/clubs?${params.toString()}`,
        { signal: abortControllerRef.current.signal }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      // Process clubs with distance calculation
      const processedClubs = data
        .map(club => {
          const hasValidLocation = club.location?.coordinates && 
            Array.isArray(club.location.coordinates) && 
            club.location.coordinates.length === 2;
          
          if (hasValidLocation) {
            const [lng, lat] = club.location.coordinates;
            const isValidCoords = typeof lat === 'number' && typeof lng === 'number' && 
              !isNaN(lat) && !isNaN(lng) &&
              lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
            
            if (isValidCoords) {
              const distance = calculateDistance(userLocation.lat, userLocation.lng, lat, lng);
              return { ...club, distance: Math.round(distance * 10) / 10 };
            }
          }
          
          return { ...club, distance: null };
        })
        .filter(club => {
          // When searching by name, show all results
          if (filters.searchQuery && filters.searchQuery.trim()) {
            return true;
          }
          // Otherwise filter by range
          return club.distance !== null && club.distance <= rangeInMiles;
        })
        .sort((a, b) => {
          if (a.distance === null && b.distance === null) return 0;
          if (a.distance === null) return 1;
          if (b.distance === null) return -1;
          return a.distance - b.distance;
        });

      setClubs(processedClubs);

      // Fetch programs for all clubs
      if (processedClubs.length > 0) {
        await fetchProgramsForClubs(processedClubs.map(c => c._id));
      } else {
        setClubPrograms({});
      }

    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching clubs:', err);
        setError(err.message);
        setClubs([]);
      }
    } finally {
      setLoading(false);
    }
  }, [userLocation, rangeInMiles, filters.sport, filters.searchQuery, enabled, calculateDistance]);

  // Fetch programs for a list of clubs
  const fetchProgramsForClubs = useCallback(async (clubIds) => {
    if (!clubIds || clubIds.length === 0) return;

    setProgramsLoading(true);

    try {
      const programsMap = await getProgramsForClubs(clubIds);
      setClubPrograms(programsMap);

      // Collect all program IDs for capacity fetch
      const allProgramIds = Object.values(programsMap)
        .flat()
        .filter(p => p && p._id)
        .map(p => p._id);

      // Fetch capacity for all programs (limited to first 50 to avoid too many requests)
      if (allProgramIds.length > 0) {
        const limitedIds = allProgramIds.slice(0, 50);
        const capacities = await getCapacityForPrograms(limitedIds);
        setCapacityMap(capacities);
      }

    } catch (err) {
      console.error('Error fetching programs:', err);
    } finally {
      setProgramsLoading(false);
    }
  }, []);

  // Debounced fetch when filters change
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchClubs();
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [fetchClubs, fetchProgramsForClubs]);

  // Update a single filter
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Update multiple filters at once
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Reset filters to default
  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_EXPLORE_FILTERS);
  }, []);

  // Clear a specific filter
  const clearFilter = useCallback((key) => {
    setFilters(prev => ({ ...prev, [key]: DEFAULT_EXPLORE_FILTERS[key] }));
  }, []);

  // Get active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.sport && filters.sport !== 'All') count++;
    if (filters.ageGroup && filters.ageGroup !== 'all') count++;
    if (filters.priceRange && filters.priceRange !== 'all') count++;
    if (filters.startDate && filters.startDate !== 'all') count++;
    if (filters.hasOpenings) count++;
    return count;
  }, [filters]);

  // Get clubs with their filtered programs
  const clubsWithPrograms = useMemo(() => {
    return clubs.map(club => {
      const allPrograms = clubPrograms[club._id] || [];
      
      // Apply filters to programs
      const filteredPrograms = filterPrograms(allPrograms, filters, capacityMap);
      
      return {
        ...club,
        programs: filteredPrograms,
        allProgramsCount: allPrograms.length,
        matchingProgramsCount: filteredPrograms.length
      };
    }).filter(club => {
      // Only show clubs that have matching programs (unless no program filters applied)
      const hasProgramFilters = 
        filters.ageGroup !== 'all' || 
        filters.priceRange !== 'all' || 
        filters.startDate !== 'all' ||
        filters.hasOpenings;
      
      if (hasProgramFilters) {
        return club.matchingProgramsCount > 0;
      }
      
      return true;
    });
  }, [clubs, clubPrograms, filters, capacityMap]);

  // Total counts
  const totalStats = useMemo(() => {
    const totalClubs = clubsWithPrograms.length;
    const totalPrograms = clubsWithPrograms.reduce(
      (sum, club) => sum + club.matchingProgramsCount, 
      0
    );
    return { totalClubs, totalPrograms };
  }, [clubsWithPrograms]);

  // Refresh data
  const refresh = useCallback(() => {
    fetchClubs();
  }, [fetchClubs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    // Data
    clubs: clubsWithPrograms,
    capacityMap,
    
    // Filters
    filters,
    updateFilter,
    updateFilters,
    resetFilters,
    clearFilter,
    activeFiltersCount,
    
    // Loading states
    loading,
    programsLoading,
    isLoading: loading || programsLoading,
    
    // Error
    error,
    
    // Stats
    totalStats,
    
    // Actions
    refresh
  };
};

export default useExploreData;










